import { v4 as uuidv4 } from "uuid";
import {
  getDocs,
  orderBy,
  query,
  runTransaction,
} from "firebase/firestore";

import {
  createInitialPlanDraft,
  serializePlanDraftForSave,
} from "../../nutrition/plans/nutritionPlanLogic";
import {
  assertNutritionLimit,
  validatePlanDraftLimits,
} from "../../nutrition/nutritionLimits";
import {
  createFatalSyncError,
  SyncErrorKind,
} from "../../sync/SyncDomainController";
import {
  clonePlainValue,
  diffTopLevelPaths,
  mergeServerIntoLocalByTouchedPaths,
} from "./OptimisticNutritionEntity";
import {
  CLIENT_NUTRITION_STATUS_ARCHIVED,
} from "./nutritionPaths";

export function normalizePlanId(value, fallback = uuidv4()) {
  return String(value ?? fallback).trim().toLowerCase();
}

export function createPlanEntityKey(scopeKey, planId) {
  return `nutrition:${String(scopeKey ?? "plan-scope").trim()}:plan:${normalizePlanId(planId, "plan")}`;
}

export function createPlanReadKey(scopeKey, planId, suffix) {
  return `nutrition:${String(scopeKey ?? "plan-scope").trim()}:plan:${normalizePlanId(planId, "plan")}:${String(suffix ?? "read").trim()}`;
}

export function isPlanReadKey(scopeKey, readKey) {
  return String(readKey ?? "").startsWith(
    `nutrition:${String(scopeKey ?? "plan-scope").trim()}:plan:`,
  );
}

export function noop() {}

export function areEqualByJson(leftValue, rightValue) {
  return JSON.stringify(leftValue ?? null) === JSON.stringify(rightValue ?? null);
}

export function sortMealsByOrder(meals) {
  return [...(Array.isArray(meals) ? meals : [])].sort(
    (leftMeal, rightMeal) => Number(leftMeal?.order ?? 0) - Number(rightMeal?.order ?? 0),
  );
}

export function sortClientEditablePlans(plans) {
  return [...(Array.isArray(plans) ? plans : [])].sort((leftPlan, rightPlan) => {
    const leftIsArchived =
      String(leftPlan?.status ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED;
    const rightIsArchived =
      String(rightPlan?.status ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED;

    if (leftIsArchived !== rightIsArchived) {
      return leftIsArchived ? 1 : -1;
    }

    const leftUpdatedAt = new Date(
      leftPlan?.updatedAt?.toDate?.() ??
        leftPlan?.updatedAt ??
        leftPlan?.createdAt?.toDate?.() ??
        leftPlan?.createdAt ??
        0,
    ).getTime();
    const rightUpdatedAt = new Date(
      rightPlan?.updatedAt?.toDate?.() ??
        rightPlan?.updatedAt ??
        rightPlan?.createdAt?.toDate?.() ??
        rightPlan?.createdAt ??
        0,
    ).getTime();

    if (rightUpdatedAt !== leftUpdatedAt) {
      return rightUpdatedAt - leftUpdatedAt;
    }

    return String(leftPlan?.name ?? "").localeCompare(String(rightPlan?.name ?? ""), undefined, {
      sensitivity: "base",
    });
  });
}

export function collectDocs(snapshot) {
  if (typeof snapshot?.docChanges === "function") {
    return snapshot.docChanges();
  }

  if (Array.isArray(snapshot?.docs)) {
    return snapshot.docs.map((docSnapshot) => ({
      type: "added",
      doc: docSnapshot,
    }));
  }

  return [];
}

export function assertMatchingRevision(snapshot, expectedRev) {
  if (expectedRev == null) {
    return;
  }

  const currentRev = Number(snapshot?.data()?._rev ?? 0);
  if (currentRev !== Number(expectedRev)) {
    const error = new Error("Nutrition document revision mismatch");
    error.syncErrorKind = SyncErrorKind.BLOCKED;
    error.currentRev = currentRev;
    error.expectedRev = Number(expectedRev);
    throw error;
  }
}

export function buildPlanDraftForMerge(planRecord = {}, mealRecords = []) {
  const draft = createInitialPlanDraft({
    ...planRecord,
    meals: Array.isArray(mealRecords) ? mealRecords : [],
  });

  if (!Array.isArray(mealRecords) || mealRecords.length === 0) {
    draft.meals = [];
  }

  return draft;
}

export function buildPlanMealsQuery(collectionRef) {
  return query(collectionRef, orderBy("order", "asc"));
}

export function applyQueuedDraftToServerRecord(entity) {
  const touchedPaths = Array.isArray(entity?.touchedPaths) ? entity.touchedPaths : [];
  const serverDraft = entity.serverBase != null
    ? clonePlainValue(entity.serverBase)
    : entity.getDraftSnapshot();
  const localDraft = entity.getDraftSnapshot();

  return mergeServerIntoLocalByTouchedPaths(serverDraft, localDraft, touchedPaths);
}

export async function persistResolvedNutritionPlanDraft({
  db,
  planId,
  planDocRef,
  mealsQuery,
  buildMealDocRef,
  ensureRootDocument = null,
  buildLocalDraft,
  baseRev,
  touchedPaths,
  requireExistingPlan = false,
  buildMealDocs,
  buildRootRecord,
} = {}) {
  if (!db || !planId || !planDocRef || !mealsQuery || typeof buildMealDocRef !== "function") {
    throw createFatalSyncError("Cannot save nutrition plan without required persistence inputs");
  }

  if (typeof buildMealDocs !== "function" || typeof buildRootRecord !== "function") {
    throw createFatalSyncError("Cannot save nutrition plan without plan builders");
  }

  if (typeof ensureRootDocument === "function") {
    await ensureRootDocument();
  }

  const normalizedPlanId = normalizePlanId(planId, "plan");
  let mergedDraft = null;
  let nextPlanDoc = null;
  let nextMealDocs = [];

  await runTransaction(db, async (transaction) => {
    const planSnapshot = await transaction.get(planDocRef);
    const mealsDiscoverySnapshot = await getDocs(mealsQuery);
    const mealDocSnaps = await Promise.all(
      mealsDiscoverySnapshot.docs.map((mealDoc) => transaction.get(mealDoc.ref)),
    );

    if (requireExistingPlan && !planSnapshot.exists()) {
      throw createFatalSyncError("Nutrition plan not found");
    }

    assertMatchingRevision(planSnapshot, baseRev);

    const existingPlanRecord = planSnapshot.exists()
      ? {
          id: normalizedPlanId,
          ...planSnapshot.data(),
        }
      : {
          id: normalizedPlanId,
        };
    const existingMealRecords = sortMealsByOrder(
      mealDocSnaps
        .filter((mealSnapshot) => mealSnapshot.exists())
        .map((mealSnapshot) => ({
          id: mealSnapshot.id,
          ...mealSnapshot.data(),
        })),
    );

    const serverDraft = buildPlanDraftForMerge(existingPlanRecord, existingMealRecords);
    const nextLocalDraftSource = clonePlainValue(serverDraft);
    const nextLocalDraftValue =
      typeof buildLocalDraft === "function"
        ? buildLocalDraft(nextLocalDraftSource, {
            planRecord: existingPlanRecord,
            mealRecords: existingMealRecords,
          })
        : nextLocalDraftSource;

    if (nextLocalDraftValue == null) {
      mergedDraft = null;
      nextPlanDoc = null;
      nextMealDocs = [];
      return;
    }

    const nextLocalDraft = clonePlainValue(nextLocalDraftValue ?? nextLocalDraftSource);
    const resolvedTouchedPaths =
      Array.isArray(touchedPaths) && touchedPaths.length > 0
        ? touchedPaths
        : diffTopLevelPaths(serverDraft, nextLocalDraft);
    const touchesMeals = resolvedTouchedPaths.includes("meals");

    mergedDraft = mergeServerIntoLocalByTouchedPaths(
      serverDraft,
      nextLocalDraft,
      resolvedTouchedPaths,
    );

    const savedPlan = serializePlanDraftForSave(mergedDraft);
    if (!savedPlan) {
      throw createFatalSyncError("Cannot save an invalid nutrition plan");
    }

    const existingMealsById = new Map(existingMealRecords.map((meal) => [meal.id, meal]));
    const serverDraftMealsById = new Map(
      (Array.isArray(serverDraft?.meals) ? serverDraft.meals : []).map((meal) => [meal.id, meal]),
    );

    nextMealDocs = touchesMeals
      ? buildMealDocs(savedPlan, mergedDraft, existingMealRecords).map((mealDoc) => {
          const nextDraftMeal = (Array.isArray(mergedDraft?.meals) ? mergedDraft.meals : []).find(
            (meal) => meal.id === mealDoc.id,
          );
          const previousDraftMeal = serverDraftMealsById.get(mealDoc.id) ?? null;
          if (
            previousDraftMeal &&
            nextDraftMeal &&
            areEqualByJson(previousDraftMeal, nextDraftMeal)
          ) {
            return clonePlainValue(existingMealsById.get(mealDoc.id) ?? mealDoc);
          }

          return mealDoc;
        })
      : existingMealRecords.map((meal) => clonePlainValue(meal));

    assertNutritionLimit(
      validatePlanDraftLimits({
        name: savedPlan.name,
        notes: savedPlan.notes,
        meals: nextMealDocs,
      }),
    );

    const nextMealsById = new Map(nextMealDocs.map((meal) => [meal.id, meal]));
    const nextRev = Number(existingPlanRecord?._rev ?? 0) + 1;

    nextPlanDoc = buildRootRecord({
      savedPlan: {
        ...savedPlan,
        id: normalizedPlanId,
      },
      mergedDraft,
      existingPlanRecord,
      nextMealDocs,
      nextRev,
    });

    transaction.set(planDocRef, nextPlanDoc);

    existingMealsById.forEach((existingMeal, mealId) => {
      if (!nextMealsById.has(mealId)) {
        transaction.delete(buildMealDocRef(normalizedPlanId, mealId));
      }
    });

    nextMealsById.forEach((mealDoc, mealId) => {
      const existingMeal = existingMealsById.get(mealId);
      if (!areEqualByJson(existingMeal, mealDoc)) {
        transaction.set(buildMealDocRef(normalizedPlanId, mealId), mealDoc);
      }
    });
  });

  if (!nextPlanDoc) {
    return null;
  }

  return {
    planDoc: nextPlanDoc,
    mealDocs: nextMealDocs,
    submittedDraft: mergedDraft,
    _rev: Number(nextPlanDoc?._rev ?? 0) || 0,
  };
}
